import axios from "axios";

import {
  getConfig,
  getLendingApiBaseUrl,
  getCorvusBaseUrl,
  getOMUBorrowerBaseUrl,
} from "utils/config";
import { getMessageID } from "helpers/utils";
import { AGREEMENT_TYPE } from "constants/omuAgreementType";
import { OAUTH_CALLBACK_PATH } from "constants/auth";

const TIMEOUT = 10000;

const getAuthBaseUrl = () => getConfig("GRIFFIN_USER_LENDING_BASE_URL");

// --- Auth ---

export const getRedirectUri = () =>
  `${window.location.origin}${OAUTH_CALLBACK_PATH}`;

export const getLogoutUri = () =>
  `${getAuthBaseUrl()}/accounts/logout?next_page=${window.location.origin}`;

export const whoami = () =>
  axios.get(`${getLendingApiBaseUrl()}/apis/whoami`, {
    withCredentials: true,
    timeout: TIMEOUT,
  });

export const pkceInit = (redirectUri) =>
  axios.get(`${getAuthBaseUrl()}/accounts/pkce/init/`, {
    params: {
      redirect_uri: redirectUri,
      client_id: getConfig("GRIFFIN_CLIENT_ID"),
    },
    timeout: TIMEOUT,
  });

// --- Register ---

export const submitRegisterBorrower = (data) =>
  axios.post(`${getCorvusBaseUrl()}/v1/taralite-com/omu/register`, data, {
    headers: {
      "Content-Type": "application/json",
    },
    params: { msg_id: getMessageID() },
  });

// --- KYB Application ---

export const getOMUProfile = () =>
  axios.get(`${getOMUBorrowerBaseUrl()}/v2/omu/profile`, {
    withCredentials: true,
    params: { msg_id: getMessageID() },
  });

export const getApplicationDetails = (params = {}) =>
  axios.get(`${getOMUBorrowerBaseUrl()}/v2/omu/application/details`, {
    withCredentials: true,
    params: { msg_id: getMessageID(), ...params },
  });

export const getLocations = (level, parentId) =>
  axios.get(`${getOMUBorrowerBaseUrl()}/v2/omu/locations`, {
    withCredentials: true,
    params: {
      msg_id: getMessageID(),
      level,
      ...(parentId ? { parent_id: parentId } : {}),
    },
  });

export const getBanks = () =>
  axios.get(`${getOMUBorrowerBaseUrl()}/v2/omu/banks`, {
    withCredentials: true,
  });

export const updateApplicationDetails = (data) =>
  axios.put(`${getOMUBorrowerBaseUrl()}/v2/omu/application/details`, data, {
    withCredentials: true,
    params: { msg_id: getMessageID() },
  });

export const uploadDocument = (documentType, file) => {
  const formData = new FormData();
  formData.append("document_type", documentType);
  formData.append("file", file, file.name);
  return axios.post(
    `${getOMUBorrowerBaseUrl()}/v2/omu/application/document`,
    formData,
    {
      withCredentials: true,
      params: { msg_id: getMessageID() },
    },
  );
};

// --- OMU Dashboard / Submissions ---

export const getLoans = ({ page = 1, limit = 20 } = {}) =>
  axios.get(`${getOMUBorrowerBaseUrl()}/v2/omu/loans`, {
    withCredentials: true,
    params: {
      msg_id: getMessageID(),
      page,
      limit,
    },
  });

export const getLoanDetail = (loanId) =>
  axios.get(
    `${getOMUBorrowerBaseUrl()}/v2/omu/loans/${encodeURIComponent(loanId)}`,
    {
      withCredentials: true,
      params: { msg_id: getMessageID() },
    },
  );

export const viewLoan = (loanId, agreementType = AGREEMENT_TYPE.LOAN) =>
  axios.get(`${getOMUBorrowerBaseUrl()}/v2/omu/agreement/document`, {
    withCredentials: true,
    params: {
      msg_id: getMessageID(),
      loan_id: loanId,
      agreement_type: agreementType,
    },
  });

export const getAgreementVariables = (loanId) =>
  axios.get(`${getOMUBorrowerBaseUrl()}/v2/omu/agreement/variables`, {
    withCredentials: true,
    params: {
      msg_id: getMessageID(),
      loan_id: loanId,
    },
  });

export const downloadFile = (url) => axios.get(url, { responseType: "blob" });

export const signLoan = (loanId) =>
  axios.post(
    `${getOMUBorrowerBaseUrl()}/v2/omu/agreement/sign`,
    {
      msg_id: getMessageID(),
      loan_id: loanId,
    },
    {
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

export const getSubmissionDetail = (submissionId) =>
  axios.get(`${getOMUBorrowerBaseUrl()}/v2/omu/submissions/${submissionId}`, {
    withCredentials: true,
    params: { msg_id: getMessageID() },
  });

export const getSubmissions = ({ page = 1, limit = 20, search, status } = {}) =>
  axios.get(`${getOMUBorrowerBaseUrl()}/v2/omu/submissions`, {
    withCredentials: true,
    params: {
      msg_id: getMessageID(),
      page,
      limit,
      ...(search ? { search } : {}),
      ...(status ? { status } : {}),
    },
  });

export const createSubmission = (data) =>
  axios.post(`${getOMUBorrowerBaseUrl()}/v2/omu/submissions/submit`, data, {
    withCredentials: true,
    params: { msg_id: getMessageID() },
  });

export const getDraftSupportingDocuments = () =>
  axios.get(
    `${getOMUBorrowerBaseUrl()}/v2/omu/submissions/draft/supporting-documents`,
    {
      withCredentials: true,
      params: { msg_id: getMessageID() },
    },
  );

export const uploadSubmissionDocument = (file) => {
  const formData = new FormData();
  formData.append("file", file, file.name);
  return axios.post(
    `${getOMUBorrowerBaseUrl()}/v2/omu/submissions/document`,
    formData,
    {
      withCredentials: true,
      params: { msg_id: getMessageID() },
    },
  );
};
